<script setup lang="ts">
import type { BiomarkerType } from '@src/store/analysis/data-typing';

import type { TableData } from './AnalysisTable.vue';

import { OCGTerritories } from '@src/store/analysis/data-typing';

import { computed } from 'vue';

import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import AnalysisTable from './AnalysisTable.vue';

const props = defineProps<{
  viewerID: string;
}>();

// TODO: Link to actual typing in biomarker manager

const BIOMARKER_OPTIONS: BiomarkerType[] = [
  'ocgDiaMaxIntensity',
  'ocgEntropy',
  'BMOREMax',
  'BMORE',
];

const analysisStore = useAnalysisStore();
const biomarkerContainer = computed(() =>
  analysisStore.getBiomarkerContainer(),
);

const viewerIndx = computed(() => {
  const viewportStateStore = useViewportStateStore();
  return viewportStateStore.getSelectedImageIndices(props.viewerID);
});

const getBiomarker = (biomarker: BiomarkerType) => {
  if (
    viewerIndx.value.sliceIdx === null
    || viewerIndx.value.sliceIdx === undefined
  ) {
    return null;
  }
  const currentStudyStore = useCurrentStudyStore();
  const seriesUID = currentStudyStore.getSeriesUID(viewerIndx.value.seriesIdx);

  const biomarkerData = biomarkerContainer.value?.getAHADataBySliceIndexAndType(
    seriesUID,
    viewerIndx.value.sliceIdx,
    biomarker,
  );

  if (!biomarkerData || !biomarkerData[0]) {
    return null;
  }
  return biomarkerData[0];
};

const getBiomarkerNormalRange = (biomarker: BiomarkerType) => {
  const range
    = biomarkerContainer.value?.getBiomarkerNormalRangeLabel(biomarker);

  if (!range) {
    return '-';
  }

  return range;
};

const formatValue = (value: number | null) =>
  value === null ? '-' : value.toFixed(2);

const tableData = computed<TableData>(() => {
  const rows = [];
  for (let i = 0; i < BIOMARKER_OPTIONS.length; i++) {
    const biomarker = BIOMARKER_OPTIONS[i];
    const biomarkerLabel
      = biomarkerContainer.value?.getLabelFromBiomarkerType(biomarker)
        || biomarker;
    const biomarkerData = getBiomarker(biomarker);
    const row: string[] = [];
    for (let j = 0; j < OCGTerritories.length; j++) {
      const territory = OCGTerritories[j];
      const data = biomarkerData?.getTerritoryMedian(territory);

      if (data === undefined) {
        row.push('-');
        continue;
      }
      const { result, std } = data;
      row.push(`${formatValue(result)} ± ${formatValue(std)}`);
    }
    const normalRange = getBiomarkerNormalRange(biomarker);
    rows.push([biomarkerLabel, ...row, normalRange]);
  }

  return {
    title: 'Biomarker Values',
    headers: ['Biomarker', ...OCGTerritories, 'Normal Range*'],
    rows,
  };
});
</script>

<template>
  <div>
    <AnalysisTable :data="tableData" />
    <p class="normal-range-note">
      *Normal ranges are subject to change as we continue validating our
      biomarkers against larger datasets.
    </p>
  </div>
</template>

<style scoped>
.normal-range-note {
  margin-top: 0.5rem;
  margin-bottom: 0;
  font-size: 0.75rem;
  font-style: italic;
  color: var(--md-on-surface-variant);
}
</style>
